import React, { useContext, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import Select from 'react-select';

import './MapBuilder.css';

import { MapBuilderContext } from './MapBuilder.context';
import { ExpandableRow } from './ExpandableRow';
import {
	REACT_SELECT_CLASSNAMES,
	PAGINATION_COMPONENT_OPTIONS,
	FILTER_INITIAL_DATA,
	DATA_TABLE_CUSTOM_STYLES,
} from './MapBuilder.constants';
import { useDataTable, useGetFilterOptions } from './MapBuilder.hooks';

import { useGetPlots, getPropertyTitle } from '../App.hooks';
import { clone } from '../App.constants';
import { LanguageContext } from '../../../../i18n/LanguageContext';

export function MapBuilderComponent() {
	const lang = useContext(LanguageContext);
	const [filter, setFilter] = useState({});
	const { loading, error, plots, setPlots } = useGetPlots();
	const { columns, data } = useDataTable(plots, lang['LANG']);

	const updatePlotPath = ({ plot, path }) => {
		setPlots(
			clone(plots).map((p) => {
				if (p.id === plot.id) {
					p.path = path;
				}
				return p;
			}),
		);
	};

	if (loading !== false) {
		return <div className="map-builder loading">{lang['LOADING']}</div>;
	}

	if (error) {
		return <div className="map-builder error">{error.message}</div>;
	}

	return (
		<MapBuilderContext.Provider
			value={{
				filter,
				setFilter,
				plots,
				updatePlotPath,
			}}
		>
			<div className="map-builder">
				<div className="wrapper vertical">
					<div className="filter-bar-container">
						<FilterBar />
					</div>
					<div className="map-builder-container">
						<div>
							<DataTable
								columns={columns}
								data={data.filter(filterData(filter))}
								pagination
								paginationPerPage={12}
								defaultSortFieldId={1}
								customStyles={DATA_TABLE_CUSTOM_STYLES}
								expandableRows
								expandableRowsComponent={ExpandableRow}
								paginationComponentOptions={
									PAGINATION_COMPONENT_OPTIONS
								}
							/>
						</div>
					</div>
				</div>
			</div>
		</MapBuilderContext.Provider>
	);
}

function filterData(filter) {
	return (row) => {
		if (!Object.keys(filter).length) {
			return true;
		}
		// eslint-disable-next-line no-unused-vars
		for (const k in filter) {
			if (filter[k] && `${row[k]}` !== `${filter[k]}`) {
				return false;
			}
		}

		return true;
	};
}

function FilterBar() {
	const lang = useContext(LanguageContext);
	const { setFilter } = useContext(MapBuilderContext);
	const [isOpen, setIsOpen] = useState(false);

	const options = useGetFilterOptions();

	const [data, setData] = useState(FILTER_INITIAL_DATA);

	useEffect(() => {
		const filter = {};
		// eslint-disable-next-line no-unused-vars
		for (const k in data) {
			const value = data[k];
			if (!value) {
				continue;
			}
			filter[k] = value;
		}

		setFilter(filter);
	}, [data, setFilter]);

	const onSubmit = (e) => {
		e.preventDefault();
		const filter = {};
		for (const k in data) {
			const value = data[k];
			if (!value) {
				continue;
			}
			filter[k] = value;
		}

		setFilter(filter);
	};

	const handleChange = (property) => {
		return (e) => {
			if (!e) {
				setData({ ...data, [property]: e });
				return;
			}
			const { value } = e;
			setData({ ...data, [property]: value });
		};
	};

	if (!options) {
		return null;
	}

	const components = Object.keys(FILTER_INITIAL_DATA).map((k) => (
		<div key={k}>
			<label htmlFor={`input-${k}`}>{getPropertyTitle(k, lang['LANG'])}</label>
			<Select
				id={`input-${k}`}
				type="text"
				name={k}
				classNamePrefix="react-select"
				className={`filter-bar-input`}
				classNames={REACT_SELECT_CLASSNAMES}
				defaultValue={data[k]}
				onChange={handleChange(k)}
				isClearable={true}
				options={options[k]}
				placeholder={lang['SELECT']}
			/>
		</div>
	));

	return (
		<form
			className={`filter-bar ${isOpen ? '' : 'close'}`}
			onSubmit={onSubmit}
		>
			<div className="title">
				<h4>{lang['FILTERS']}</h4>
				<div>
					<button
						onClick={() => {
							setIsOpen(!isOpen);
						}}
					>
						{isOpen ? <UpIcon /> : <DownIcon />}
					</button>
				</div>
			</div>
			<div className="inputs">{isOpen ? components : null}</div>
		</form>
	);
}

function DownIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polyline points="6 9 12 15 18 9"></polyline>
		</svg>
	);
}

function UpIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polyline points="18 15 12 9 6 15"></polyline>
		</svg>
	);
}
